import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
  output,
} from '@angular/core';

import {
  Icon,
  TableBuilderFactory,
  TableComponent,
  TableConfig,
  TableLazyLoad,
} from '@web/shared/ui';
import { AdminDictionaryListAction } from '../../enums/admin-dictionary-list-action';
import { AdminDictionaryListItemAction } from '../../enums/admin-dictionary-list-item-action';
import { AdminDictionaryCategoryBoxDataSourceService } from '../../services/admin-dictionary-category-box-data-source.service';
import { DictionaryCollectionFilter } from '../../../shared/models/dictionary-collection-filter';
import { DictionaryCollectionItem } from '../../../shared/models/dictionary-collection-item';
import { Route, RouteParam } from '@web/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { of } from 'rxjs';

@Component({
  selector: 'app-admin-dictionary-box-category-list',
  imports: [TableComponent],
  templateUrl: './admin-dictionary-box-category-list.component.html',
  styleUrl: './admin-dictionary-box-category-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminDictionaryBoxCategoryListComponent {
  /* prettier-ignore */
  private readonly tableBuilder = inject(TableBuilderFactory<DictionaryCollectionItem, DictionaryCollectionFilter, AdminDictionaryListItemAction, never, AdminDictionaryListAction>).create();
  /* prettier-ignore */
  private readonly dictionaryDataSource = inject(AdminDictionaryCategoryBoxDataSourceService);

  public readonly createCategoryVisibility = input<boolean>(false);
  public readonly removeSuccessEvent = output<void>();

  protected tableConfig = this.buildTableConfig();

  private readonly destroyRef = inject(DestroyRef);

  private buildTableConfig(): TableConfig<
    DictionaryCollectionItem,
    DictionaryCollectionFilter,
    AdminDictionaryListItemAction,
    never,
    AdminDictionaryListAction
  > {
    return this.tableBuilder
      .columns((builder) => [
        builder
          .initText('label')
          .filterKey('label')
          .filterable(true)
          .width('25rem')
          .label('Etykieta')
          .build(),
        builder //
          .initText('slug')
          .width('25rem')
          .label('Slug')
          .build(),
        builder
          .initDate('createdAt')
          .filterKey('label')
          .filterable(true)
          .label('Data dodania')
          .build(),
      ])
      .filters((builder) => [
        builder.initText('label').build(),
        builder.initDateRange('createdAt').build(),
      ])
      .itemActions((builder) => [
        builder
          .initRouterLink(AdminDictionaryListItemAction.Edit)
          .visibility((model) => model.canBeEdited)
          .routerLink(
            (provider) => (model) =>
              provider.routerLink(Route.DictionaryBoxCategoryEdit, {
                [RouteParam.Guid]: model.id,
              }),
          )
          .tooltip('Edycja')
          .icon(Icon.Pencil)
          .build(),

        builder
          .initCommand(AdminDictionaryListItemAction.Remove)
          .visibility((model) => model.canBeRemoved)
          .command((item) => this.confirmAndRemove(item))
          .tooltip('Usuń')
          .icon(Icon.Trash)
          .build(),
      ])
      .actions((builder) => [
        builder
          .initRouterLink(AdminDictionaryListAction.Create)
          .routerLink((provider) =>
            provider.routerLink(Route.DictionaryBoxCategoryCreate),
          )
          .visibility(() => of(this.createCategoryVisibility()))
          .label('Dodaj kategorię')
          .build(),
      ])
      .dataKey('id')
      .dataSource({
        fetch: (lazyLoad: TableLazyLoad<DictionaryCollectionFilter>) =>
          this.dictionaryDataSource.fetchCollection(lazyLoad),
      })
      .build();
  }

  private confirmAndRemove(item: DictionaryCollectionItem): void {
    this.dictionaryDataSource
      .confirmAndRemoveById(item.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.removeSuccessEvent.emit());
  }
}
